/**
 * TypePickerGrid.js — Grid picker for adding conditions, costs, or effects
 *
 * Shows a searchable grid of available types grouped by category.
 * Appears when user clicks [+ Add ...] in a PipelineSection.
 */

import { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import TypePickerCell from './TypePickerCell';

// ─── Type Metadata ──────────────────────────────────────────────────────────

const CONDITION_TYPES = [
  { type: 'resource_available', description: 'A resource (stress, hope, HP) is available', category: 'Conditions' },
  { type: 'has_token', description: 'Character has tokens of a specific type', category: 'Conditions' },
  { type: 'not_active', description: 'A session state key is NOT active', category: 'Conditions' },
  { type: 'is_active', description: 'A session state key IS active', category: 'Conditions' },
  { type: 'not_on_cooldown', description: 'Ability is not on cooldown', category: 'Conditions' },
  { type: 'hp_below', description: 'HP is below a threshold', category: 'Conditions' },
  { type: 'level_at_least', description: 'Character is at least level N', category: 'Conditions' },
  { type: 'has_character_flag', description: 'Character has a specific flag set', category: 'Conditions' },
  { type: 'not_has_character_flag', description: 'Character does NOT have a flag', category: 'Conditions' },
  { type: 'has_ancestry', description: 'Character has a specific ancestry', category: 'Conditions' },
  { type: 'has_class', description: 'Character has a specific class', category: 'Conditions' },
];

const COST_TYPES = [
  { type: 'mark_stress', description: 'Mark stress points', category: 'Costs' },
  { type: 'spend_hope', description: 'Spend hope points', category: 'Costs' },
  { type: 'spend_token', description: 'Spend tokens of a specific type', category: 'Costs' },
  { type: 'spend_hp', description: 'Spend hit points', category: 'Costs' },
  { type: 'set_cooldown', description: 'Set a cooldown timer', category: 'Costs' },
];

const EFFECT_TYPES = [
  // State
  { type: 'set_session_state', description: 'Set a session state key to a value', category: 'State' },
  { type: 'clear_session_state', description: 'Clear a session state key', category: 'State' },
  { type: 'set_character_flag', description: 'Set a persistent character flag', category: 'State' },
  { type: 'clear_character_flag', description: 'Clear a persistent character flag', category: 'State' },
  { type: 'toggle_character_flag', description: 'Toggle a character flag on/off', category: 'State' },
  // Resources
  { type: 'gain_hope', description: 'Gain hope points', category: 'Resources' },
  { type: 'clear_stress', description: 'Clear stress points', category: 'Resources' },
  { type: 'mark_stress', description: 'Mark stress as a consequence', category: 'Resources' },
  { type: 'heal_hp', description: 'Heal hit points', category: 'Resources' },
  { type: 'gain_token', description: 'Gain tokens of a specific type', category: 'Resources' },
  { type: 'reset_token', description: 'Reset token count to max', category: 'Resources' },
  { type: 'initialize_token', description: 'Initialize a new token tracker', category: 'Resources' },
  // Combat
  { type: 'grant_armor', description: 'Grant temporary armor', category: 'Combat' },
  { type: 'set_advantage', description: 'Set advantage on next roll', category: 'Combat' },
  { type: 'add_bonus_dice', description: 'Add bonus dice to next roll', category: 'Combat' },
  { type: 'trigger_spell_attack', description: 'Trigger a spell attack roll', category: 'Combat' },
  { type: 'trigger_trait_roll', description: 'Trigger a trait roll', category: 'Combat' },
  { type: 'trigger_weapon_roll', description: 'Trigger a weapon roll', category: 'Combat' },
  // Social
  { type: 'heal_ally', description: 'Heal an ally\'s HP', category: 'Social' },
  { type: 'clear_ally_stress', description: 'Clear an ally\'s stress', category: 'Social' },
  { type: 'grant_ally_hope', description: 'Grant hope to an ally', category: 'Social' },
  { type: 'grant_ally_armor', description: 'Grant armor to an ally', category: 'Social' },
  // UI
  { type: 'show_toast', description: 'Show a notification toast', category: 'UI' },
  { type: 'switch_tab', description: 'Switch to a different tab', category: 'UI' },
  { type: 'roll_local_dice', description: 'Roll dice locally (no server)', category: 'UI' },
];

const TYPE_MAP = {
  condition: CONDITION_TYPES,
  cost: COST_TYPES,
  effect: EFFECT_TYPES,
};

// Default values when adding a new item
const DEFAULT_VALUES = {
  // Conditions
  resource_available: { type: 'resource_available', resource: 'stress' },
  has_token: { type: 'has_token', tokenId: '' },
  not_active: { type: 'not_active', stateKey: '' },
  is_active: { type: 'is_active', stateKey: '' },
  not_on_cooldown: { type: 'not_on_cooldown', cooldownKey: '' },
  hp_below: { type: 'hp_below', threshold: 5 },
  level_at_least: { type: 'level_at_least', level: 1 },
  has_character_flag: { type: 'has_character_flag', flag: '' },
  not_has_character_flag: { type: 'not_has_character_flag', flag: '' },
  has_ancestry: { type: 'has_ancestry', ancestry: '' },
  has_class: { type: 'has_class', class: '' },
  // Costs
  mark_stress: { type: 'mark_stress', amount: 1 },
  spend_hope: { type: 'spend_hope', amount: 1 },
  spend_token: { type: 'spend_token', tokenId: '', amount: 1 },
  spend_hp: { type: 'spend_hp', amount: 1 },
  set_cooldown: { type: 'set_cooldown', cooldownKey: '', duration: 1 },
  // Effects
  set_session_state: { type: 'set_session_state', stateKey: '', value: true },
  clear_session_state: { type: 'clear_session_state', stateKey: '' },
  set_character_flag: { type: 'set_character_flag', flag: '' },
  clear_character_flag: { type: 'clear_character_flag', flag: '' },
  toggle_character_flag: { type: 'toggle_character_flag', flag: '' },
  gain_hope: { type: 'gain_hope', amount: 1 },
  clear_stress: { type: 'clear_stress', amount: 1 },
  heal_hp: { type: 'heal_hp', amount: 1 },
  gain_token: { type: 'gain_token', tokenId: '', amount: 1 },
  reset_token: { type: 'reset_token', tokenId: '' },
  initialize_token: { type: 'initialize_token', tokenId: '' },
  grant_armor: { type: 'grant_armor', amount: 1 },
  set_advantage: { type: 'set_advantage' },
  add_bonus_dice: { type: 'add_bonus_dice', amount: 1 },
  trigger_spell_attack: { type: 'trigger_spell_attack' },
  trigger_trait_roll: { type: 'trigger_trait_roll' },
  trigger_weapon_roll: { type: 'trigger_weapon_roll' },
  heal_ally: { type: 'heal_ally', amount: 1 },
  clear_ally_stress: { type: 'clear_ally_stress', amount: 1 },
  grant_ally_hope: { type: 'grant_ally_hope', amount: 1 },
  grant_ally_armor: { type: 'grant_ally_armor', amount: 1 },
  show_toast: { type: 'show_toast', message: '' },
  switch_tab: { type: 'switch_tab', tab: '' },
  roll_local_dice: { type: 'roll_local_dice' },
};

export { DEFAULT_VALUES };

const TypePickerGrid = ({ chipType, onSelect, onCancel }) => {
  const [filter, setFilter] = useState('');
  const types = TYPE_MAP[chipType] || [];

  const filtered = useMemo(() => {
    if (!filter) return types;
    const lc = filter.toLowerCase();
    return types.filter(
      (t) => t.type.includes(lc) || t.description.toLowerCase().includes(lc)
    );
  }, [types, filter]);

  // Group by category
  const grouped = useMemo(() => {
    const groups = {};
    for (const item of filtered) {
      const cat = item.category;
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    }
    return groups;
  }, [filtered]);

  const handleSelect = (type) => {
    const defaults = DEFAULT_VALUES[type] || { type };
    onSelect({ ...defaults });
  };

  return (
    <div className="mt-2 bg-gray-900 border border-gray-600 rounded-lg p-3 shadow-lg">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-300">
          Add {chipType}
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-white p-0.5"
        >
          <X size={14} />
        </button>
      </div>

      <input
        type="text"
        placeholder="Search..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className={cn(
          'w-full bg-gray-800 border border-gray-600 rounded-md',
          'px-3 py-1.5 text-white text-sm mb-2',
          'focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none',
          'placeholder:text-gray-500'
        )}
      />

      <div className="max-h-64 overflow-y-auto">
        {Object.entries(grouped).map(([category, items]) => (
          <div key={category} className="mb-2">
            {Object.keys(grouped).length > 1 && (
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 px-1">
                {category}
              </div>
            )}
            <div className="grid grid-cols-2 gap-1.5">
              {items.map((item) => (
                <TypePickerCell
                  key={item.type}
                  type={item.type}
                  description={item.description}
                  onClick={handleSelect}
                />
              ))}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-sm text-gray-500 text-center py-3">
            No matching types found.
          </div>
        )}
      </div>
    </div>
  );
};

export default TypePickerGrid;
